import { useCallback, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useMachine, useSelector } from "@xstate/react";
import {
  prescriptionFlowMachine,
  type PrescriptionFlowStateId,
} from "./machine";
import { PrescriptionFlowActorProvider } from "./context";
import { useFlowBackHandler, useOptionalSelfService } from "../../context";
import { frontdeskDiagnosticLogger } from "../../../../lib/diagnostic-logger";
import {
  flowPhaseProgress,
  usePublishFlowPhaseProgress,
  type FlowPhasePlacement,
} from "../scheduler/flow-phase-progress";
import { PrescriptionCardCheckStep } from "./steps/PrescriptionCardCheckStep";
import { MedicationSelectionStep } from "./steps/MedicationSelectionStep";
import { RequestReviewStep } from "./steps/RequestReviewStep";
import { RequestConfirmationStep } from "./steps/RequestConfirmationStep";
import { NoPatientFoundStep } from "./steps/NoPatientFoundStep";
import { PatientLookupStep } from "./steps/PatientLookupStep";
import { ManualPrescriptionRequestStep } from "./steps/ManualPrescriptionRequestStep";
import { ManualPrescriptionHandoffStep } from "./steps/ManualPrescriptionHandoffStep";

/** First state of the prescription flow — back from here exits the flow */
const FIRST_STATE: PrescriptionFlowStateId = "cardCheck";

/**
 * Outcome screens: they own their exits, so the footer "Zurück" is hidden
 * (KO-05, KO-06). `noPatientFound` offers "Erneut versuchen" itself.
 */
const TERMINAL_STATES: ReadonlySet<PrescriptionFlowStateId> = new Set([
  "done",
  "requestConfirmation",
  "manualHandoff",
  "noPatientFound",
]);

/**
 * Where each state sits in "Anmelden · Anfrage · Prüfen" (KO-20). The manual
 * path has the same milestones as the card path; handoffs hide the stepper.
 */
function prescriptionPhasePlacement(state: PrescriptionFlowStateId): FlowPhasePlacement {
  switch (state) {
    case "cardCheck":
    case "patientLookup":
      return { phase: 1 };
    case "medicationSelection":
    case "manualMedicationRequest":
      return { phase: 2 };
    case "requestReview":
    case "requestSubmitting":
      return { phase: 3 };
    case "requestConfirmation":
    case "done":
      return { kind: "complete" };
    case "noPatientFound":
    case "manualHandoff":
      return { kind: "hidden" };
  }
}

/**
 * Compile-time proof that every prescription state has a composition. At
 * runtime an unknown state renders nothing rather than a debug string (FC-21).
 */
function assertNoUnrenderedPrescriptionState(state: never): null {
  frontdeskDiagnosticLogger.error(
    "[Prescription] no composition for machine state; rendering nothing",
    { state },
  );
  return null;
}

interface PrescriptionFlowProps {
  onComplete?: () => void;
}

/**
 * Leaves the flow once the machine reaches `done` (the confirmation screen is
 * the only success screen, KO-82).
 */
function ExitOnDone({ onComplete }: { readonly onComplete: () => void }) {
  useEffect(
    function exitFinishedPrescriptionFlow() {
      onComplete();
    },
    [onComplete],
  );
  return null;
}

function PrescriptionCurrentStep({
  currentState,
  onComplete,
}: {
  currentState: PrescriptionFlowStateId;
  onComplete: () => void;
}) {
  switch (currentState) {
    case "cardCheck":
      return <PrescriptionCardCheckStep />;

    case "medicationSelection":
      return <MedicationSelectionStep />;

    case "patientLookup":
      return <PatientLookupStep />;

    case "manualMedicationRequest":
      return <ManualPrescriptionRequestStep />;

    case "requestReview":
    case "requestSubmitting":
      return <RequestReviewStep />;

    case "requestConfirmation":
      return <RequestConfirmationStep />;

    case "manualHandoff":
      return <ManualPrescriptionHandoffStep />;

    case "noPatientFound":
      return <NoPatientFoundStep />;

    case "done":
      return <ExitOnDone onComplete={onComplete} />;
  }
  return assertNoUnrenderedPrescriptionState(currentState);
}

/**
 * Prescription flow orchestrator component — combined view + order
 *
 * Uses XState to manage the multi-step prescription flow:
 * 1. Identification (card, or the shared name lookup)
 * 2. Request form with inline history helpers
 * 3. Review & submit
 * 4. One confirmation screen, then back to the hub
 *
 * @since 1.0.0
 * @example
 * ```tsx
 * <PrescriptionFlow onComplete={() => setActiveFlow(null)} />
 * ```
 */
export function PrescriptionFlow({ onComplete }: PrescriptionFlowProps) {
  const { t } = useTranslation();
  const [, send, actorRef] = useMachine(prescriptionFlowMachine);
  const setBackHidden = useOptionalSelfService()?.setBackHidden;

  const currentState = useSelector(
    actorRef,
    (s) => s.value as PrescriptionFlowStateId,
  );
  const isTerminalState = TERMINAL_STATES.has(currentState);

  // Hide back button for terminal states
  useEffect(
    function syncBackHidden() {
      setBackHidden?.(isTerminalState);
    },
    [isTerminalState, setBackHidden],
  );

  usePublishFlowPhaseProgress(
    flowPhaseProgress(
      [
        t("self-service.prescription.phase.identify"),
        t("self-service.prescription.phase.request"),
        t("self-service.prescription.phase.review"),
      ],
      prescriptionPhasePlacement(currentState),
    ),
  );

  // Register back handler with parent
  const handleBack = useCallback(() => {
    if (currentState === FIRST_STATE) {
      onComplete?.();
    } else {
      send({ type: "BACK" });
    }
  }, [currentState, onComplete, send]);
  const handleComplete = useCallback(() => {
    if (onComplete) onComplete();
    else send({ type: "DONE" });
  }, [onComplete, send]);

  useFlowBackHandler(handleBack);

  return (
    <PrescriptionFlowActorProvider actor={actorRef} onExit={onComplete}>
      <PrescriptionCurrentStep
        currentState={currentState}
        onComplete={handleComplete}
      />
    </PrescriptionFlowActorProvider>
  );
}
